import {
  CurrencyCode, EnergySource, HvacArea,
} from '@belimo-retrofit-portal/logic';
import { FormController } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'src/migration/react-intl';
import { Button } from 'src/modules/common/components/Button';
import { EnergyPriceUnit } from 'src/modules/common/components/EnergyPriceUnit';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { DECIMAL_PLACES, MAX_TRILLION } from 'src/modules/common/constants/decimal';
import {
  CURRENCY_CODE_OPTIONS_SORTED,
} from 'src/modules/common/constants/options';
import { CurrencyExchangeIcon } from 'src/modules/common/icons/CurrencyExchangeIcon';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { DecimalField } from 'src/modules/form/components/DecimalField';
import { DropdownField } from 'src/modules/form/components/DropdownField';
import { FormError } from 'src/modules/form/types/FormError';
import { ensureNonNull } from 'src/modules/form/utils/transform';
import { HvacFormAssessmentData, HvacFormData } from 'src/modules/hvac/types/HvacFormData';
import { PROJECT_CURRENCY_DEFAULT_DATA } from 'src/modules/project-new/constants/projectData';

type Props = {
  readonly form: FormController<HvacFormData, FormError>;
  readonly heatingEnabled: boolean;
  readonly coolingEnabled: boolean;
  readonly readOnly: boolean;
};

export const HvacSettingsEnergyPricesCardView = memo(({
  form,
  heatingEnabled,
  coolingEnabled,
  readOnly,
}: Props) => {
  const intl = useIntl();

  const currency = useFormWatch(form, form.field.at('currency').value.get);
  const energyInfo = useFormWatch(form.field.at('assessment').form, form.field.at('assessment').value.get);
  const visibleSources = useMemo(() => Array.from(new Set([
    ...getSystemEnergySources(energyInfo, HvacArea.HEATING, heatingEnabled),
    ...getSystemEnergySources(energyInfo, HvacArea.COOLING, coolingEnabled),
  ])), [coolingEnabled, energyInfo, heatingEnabled]);

  const resetCurrencies = useCallback(() => {
    form.change(pipe(
      form.currentState,
      form.field.at('currency').value.set(PROJECT_CURRENCY_DEFAULT_DATA),
      form.field.at('assessment').at('prices').value.set({
        [EnergySource.ELECTRICITY]: null,
        [EnergySource.GAS]: null,

        [EnergySource.HEATING_DISTRICT]: null,
        [EnergySource.HEATING_OIL]: null,
        [EnergySource.HEATING_OTHER]: null,

        [EnergySource.COOLING_DISTRICT]: null,
        [EnergySource.COOLING_OTHER]: null,
      }),
    ));
  }, [form]);

  return (
    <div className="bp-hvac-settings-view__card">
      <div className="bp-hvac-settings-view__card-header">
        <div className="bp-hvac-settings-view__card-header-contents">
          <div className="bp-hvac-settings-view__card-icon">
            <CurrencyExchangeIcon/>
          </div>

          <div className="bp-hvac-settings-view__card-title">
            <FormattedMessage id="hvac/energyPrices/title"/>
          </div>

          <Tooltip
            place="top"
            id="bp-hvac-settings-energy-prices-card-view-info-tooltip"
            value={(
              <div className="bp-hvac-settings-view__card-header-tooltip-icon">
                <FormattedMessage id="hvac/energyPrices/title/tooltip"/>
              </div>
            )}
          >
            <div
              className="bp-hvac-settings-view__tooltip-icon"
            >
              <InfoIcon/>
            </div>
          </Tooltip>
        </div>

        {!readOnly && (
          <div className="bp-hvac-settings-view__card-actions">
            <Tooltip
              id={`hvac-settings-view-${form.field.path}`}
              value={intl.formatMessage({ id: 'hvac/energy/reset' })}
            >
              <Button
                kind="ghost"
                size="sm"
                iconOnly={true}
                onClick={resetCurrencies}
                icon={(
                  <div className="bp-hvac-settings-view__card-icon">
                    <ResetIcon/>
                  </div>
                )}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div className="bp-hvac-settings-view__card-section">
        <DropdownField
          size="md"
          label={intl.formatMessage({ id: 'hvac/energy/currency' })}
          field={form.field.at('currency').transform(ensureNonNull())}
          options={CURRENCY_CODE_OPTIONS_SORTED}
          renderOption={renderCurrencyCodeOption}
          readOnly={readOnly}
        />

        {visibleSources.map((source) => (
          <DecimalField
            size="md"
            key={source}
            decimalScale={DECIMAL_PLACES}
            field={form.field.at('assessment').at('prices').at(source)}
            label={intl.formatMessage({ id: `hvac/energy/price/${source}` })}
            autoComplete="off"
            decimalPlaces={4}
            maxValue={MAX_TRILLION}
            readOnly={readOnly}
            actions={(
              <EnergyPriceUnit
                source={source}
                currency={currency}
              />
            )}
          />
        ))}
      </div>
    </div>
  );
});

function renderCurrencyCodeOption(option: CurrencyCode): React.ReactElement {
  return <FormattedMessage id={`common/currency/${option}`}/>;
}

function getSystemEnergySources(
  formData: HvacFormAssessmentData,
  systemType: HvacArea,
  systemEnabled: boolean,
): EnergySource[] {
  return systemEnabled
    ? [EnergySource.ELECTRICITY, formData.areas[systemType].thermal.energySource]
    : [EnergySource.ELECTRICITY];
}
